import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PARENT_NAV } from "./ParentDashboard";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import {
  Trash2, Package, ChevronDown, ChevronUp, Plus, Minus, CheckCircle2,
  ShoppingCart, BookOpen,
} from "lucide-react";

export default function Cart() {
  const cart = useCart();
  const { items, removeLine, setProductQty, clear, total, mrpTotal, savings, orderItems } = cart;
  const { user } = useAuth();
  const nav = useNavigate();
  const [addr, setAddr] = useState({
    student_name: user?.name || "",
    student_class: user?.student_class || "",
    mobile: user?.phone || "",
    delivery_address: "",
  });
  const [placing, setPlacing] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const validate = () => {
    const e = {};
    if (!addr.student_name?.trim() || addr.student_name.trim().length < 2)
      e.student_name = "Parent name is required";
    if (!addr.student_class?.trim())
      e.student_class = "Student class is required";
    if (!addr.mobile?.trim() || !/^[6-9]\d{9}$/.test(addr.mobile.trim().replace(/^\+?91/, "")))
      e.mobile = "Enter a valid 10-digit Indian mobile number";
    if (!addr.delivery_address?.trim() || addr.delivery_address.trim().length < 10)
      e.delivery_address = "Delivery / collection address is required";
    return e;
  };
  const [errors, setErrors] = useState({});

  const placeOrder = async () => {
    if (items.length === 0) return toast.error("Cart is empty");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return toast.error("Please fix the highlighted fields");
    setPlacing(true);
    try {
      const payload = {
        items: orderItems(),   // ← FLATTENS kits into {product_id, qty} pairs
        student_name: addr.student_name.trim(),
        student_class: addr.student_class.trim(),
        delivery_address: addr.delivery_address.trim(),
      };
      const { data } = await api.post("/orders", payload);
      setConfirmation({ id: data.id, number: data.order_number, total: data.total });
      clear();
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to place order");
    } finally { setPlacing(false); }
  };

  if (confirmation) {
    return (
      <DashboardLayout nav={PARENT_NAV} title="Order Placed">
        <div className="max-w-lg mx-auto text-center py-12" data-testid="order-confirmation">
          <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-600"/>
          </div>
          <h2 className="font-heading text-2xl font-bold text-brand-navy">Order placed successfully</h2>
          <p className="text-slate-600 mt-2">Thank you — a confirmation email is on its way.</p>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-left inline-block min-w-[280px]">
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Order ID</div>
            <div className="font-heading text-xl font-bold text-brand-navy" data-testid="order-id">{confirmation.number}</div>
            <div className="text-xs text-slate-500 mt-3 font-semibold uppercase tracking-wider">Total</div>
            <div className="font-heading text-lg font-bold text-brand-navy">₹ {Number(confirmation.total || 0).toLocaleString('en-IN')}</div>
          </div>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/parent/orders" data-testid="btn-view-orders"
                  className="px-4 py-2 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark">
              View My Orders
            </Link>
            <Link to="/parent/catalog"
                  className="px-4 py-2 rounded-md border border-slate-300 bg-white text-slate-700 font-semibold hover:bg-slate-50">
              Continue shopping
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout nav={PARENT_NAV} title="Your Cart">
      {items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="cart-empty">
          <ShoppingCart size={36} className="mx-auto text-slate-300 mb-3"/>
          <div className="text-slate-600">Your cart is empty.</div>
          <div className="mt-4 flex gap-2 justify-center">
            <Link to="/parent/book-kits" className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold">Browse Book Kits</Link>
            <Link to="/parent/catalog" className="px-4 py-2 rounded-md border border-slate-300 bg-white text-sm font-semibold">Shop Catalog</Link>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {items.map((x, idx) =>
              x.type === "kit"
                ? <KitLine key={idx} line={x} onRemove={() => removeLine(idx)}/>
                : <ProductLine key={idx} line={x} onRemove={() => removeLine(idx)}
                               onQty={(q) => setProductQty(idx, q)}/>
            )}
          </div>

          <aside className="rounded-xl border border-slate-200 bg-white p-5 h-fit">
            <div className="font-heading font-semibold text-brand-navy">Order Summary</div>
            <div className="mt-4 space-y-2 text-sm">
              {mrpTotal > total && (
                <Row label="MRP total" value={`₹${mrpTotal.toLocaleString('en-IN')}`} strike/>
              )}
              <Row label="Subtotal" value={`₹${total.toLocaleString('en-IN')}`}/>
              {savings > 0 && <Row label="You save" value={`₹${savings.toLocaleString('en-IN')}`} tone="text-emerald-700"/>}
              <Row label="Delivery" value="Free"/>
              <div className="border-t border-slate-200 my-2"/>
              <Row label="Total" value={`₹${total.toLocaleString('en-IN')}`} bold/>
            </div>

            <div className="mt-5 space-y-3">
              <Field label="Parent name" required error={errors.student_name}>
                <input value={addr.student_name}
                       data-testid="input-parent-name"
                       onChange={(e) => setAddr((a) => ({ ...a, student_name: e.target.value }))}
                       placeholder="e.g. Rahul Verma"
                       className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"/>
              </Field>
              <Field label="Student class" required error={errors.student_class}>
                <input value={addr.student_class}
                       data-testid="input-student-class"
                       onChange={(e) => setAddr((a) => ({ ...a, student_class: e.target.value }))}
                       placeholder="e.g. Class 8"
                       className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"/>
              </Field>
              <Field label="Mobile number" required error={errors.mobile}>
                <input value={addr.mobile} type="tel"
                       data-testid="input-mobile"
                       onChange={(e) => setAddr((a) => ({ ...a, mobile: e.target.value }))}
                       placeholder="10-digit Indian mobile"
                       className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"/>
              </Field>
              <Field label="Delivery / collection address" required error={errors.delivery_address}>
                <textarea value={addr.delivery_address} rows={2}
                          data-testid="input-address"
                          onChange={(e) => setAddr((a) => ({ ...a, delivery_address: e.target.value }))}
                          placeholder="Flat / house · street · city · pincode"
                          className="w-full rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"/>
              </Field>
            </div>
            <button
              data-testid="btn-place-order"
              disabled={placing}
              onClick={placeOrder}
              className="mt-5 w-full py-3 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:bg-slate-300">
              {placing ? "Placing…" : "Place Demo Order"}
            </button>
            <div className="text-[11px] text-slate-500 text-center mt-2">
              Demo transaction — no payment will be charged.
            </div>
          </aside>
        </div>
      )}
    </DashboardLayout>
  );
}

function KitLine({ line, onRemove }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`cart-line-kit-${line.kit_id}`}>
      <div className="p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-md bg-blue-50 grid place-items-center text-brand-blue flex-shrink-0">
          <Package size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-blue">Complete Book Kit</div>
          <div className="font-heading font-semibold text-brand-navy truncate">{line.kit_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {line.kit_school_name && <span>{line.kit_school_name}</span>}
            {line.kit_year_name && <span> · Year {line.kit_year_name}</span>}
            {line.kit_class && <span> · Class {line.kit_class}</span>}
            {line.kit_lang && <span> · {line.kit_lang}</span>}
            {line.kit_combo && <span> · {line.kit_combo}</span>}
          </div>
          <div className="text-xs text-slate-600 mt-1">{line.items.length} book(s)</div>
        </div>
        <div className="text-right">
          <div className="font-heading font-bold text-brand-navy">₹ {Number(line.price || 0).toLocaleString('en-IN')}</div>
          <button onClick={onRemove} data-testid={`btn-remove-kit-${line.kit_id}`}
                  className="text-rose-500 hover:text-rose-700 text-xs mt-1 inline-flex items-center gap-1">
            <Trash2 size={12}/> Remove kit
          </button>
        </div>
      </div>
      <button onClick={() => setOpen(!open)}
              data-testid={`btn-toggle-kit-${line.kit_id}`}
              className="w-full border-t border-slate-100 px-4 py-2 text-xs font-semibold text-brand-blue hover:bg-slate-50 flex items-center gap-1">
        {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
        {open ? "Hide" : "View"} kit contents
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100 bg-slate-50/50">
          {line.items.map((i) => (
            <div key={i.product_id} className="px-4 py-2 flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-white rounded overflow-hidden grid place-items-center flex-shrink-0">
                {i.image ? <img src={i.image} alt="" className="max-w-full max-h-full object-contain"
                                onError={(e)=>{e.currentTarget.src="/book-cover-unavailable.svg";}}/>
                        : <BookOpen size={14} className="text-slate-400"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 truncate">{i.name}</div>
                <div className="text-xs text-slate-500">{i.subject} · Qty {i.qty}</div>
              </div>
              <div className="text-xs text-slate-600">₹ {Number(i.price || 0).toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductLine({ line, onRemove, onQty }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4" data-testid={`cart-line-product-${line.product_id}`}>
      <div className="w-12 h-12 rounded-md bg-slate-50 grid place-items-center overflow-hidden flex-shrink-0">
        {line.image ? <img src={line.image} alt="" className="max-w-full max-h-full object-contain"
                           onError={(e)=>{e.currentTarget.src="/stationery-placeholder.svg";}}/>
                    : <BookOpen size={20} className="text-slate-400"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 text-sm truncate">{line.name}</div>
        <div className="text-xs text-slate-500">{line.category}</div>
      </div>
      <div className="flex items-center border border-slate-300 rounded-md">
        <button onClick={() => onQty(line.qty - 1)} className="p-1.5 hover:bg-slate-100"><Minus size={12}/></button>
        <span className="min-w-[24px] text-center text-sm font-semibold">{line.qty}</span>
        <button onClick={() => onQty(line.qty + 1)} className="p-1.5 hover:bg-slate-100"><Plus size={12}/></button>
      </div>
      <div className="text-right min-w-[70px]">
        <div className="font-semibold text-brand-navy text-sm">₹ {(Number(line.price) * line.qty).toLocaleString('en-IN')}</div>
        <button onClick={onRemove} className="text-rose-500 text-xs">
          <Trash2 size={12}/>
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, strike, bold, tone }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>{label}</span>
      <span className={`${bold ? "font-heading font-bold text-brand-navy text-lg" : ""} ${strike ? "line-through text-slate-400" : ""} ${tone || ""}`}>{value}</span>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
        <span>{label} {required && <span className="text-rose-500">*</span>}</span>
        {error && <span className="text-rose-500 font-normal">{error}</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
